import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useQuery} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

type AgentRow = {
  /** Integration id for the agent's app, e.g. `838`. */
  id?: number
  slug: string
  /** API `needs_authorization`; `undefined` when the server-side flag is off. */
  needs_authorization?: boolean
}

type AgentsListResponse = {
  agents?: AgentRow[]
}

/** Per-agent status returned from the per-repo agents endpoint. */
export type AgentAuthorizationStatus = {
  needsAuthorization: boolean
  integrationId?: number
}

/** Map of agent slug → status. Missing keys → fall back to the mock client. */
export type AgentAuthorizationStatuses = ReadonlyMap<string, AgentAuthorizationStatus>

const EMPTY_STATUSES: AgentAuthorizationStatuses = new Map()

/**
 * Fetches `/copilot/agent-sessions/{nwo}/agents` and exposes each agent's
 * `needs_authorization` as a slug-keyed map. Returns empty map when the flag is off.
 */
export function useAgentAuthorizationStatuses(nwo?: string): {
  data: AgentAuthorizationStatuses
  isLoading: boolean
} {
  const flagEnabled = isFeatureEnabled('copilot_swe_agent_authorization_status_ui')
  const {data, isLoading} = useQuery<AgentAuthorizationStatuses>({
    enabled: flagEnabled && Boolean(nwo),
    queryKey: ['agent-authorization-statuses', nwo],
    queryFn: async () => {
      if (!nwo) return EMPTY_STATUSES
      try {
        const response = await verifiedFetchJSON(`/copilot/agent-sessions/${nwo}/agents`)
        if (!response.ok) return EMPTY_STATUSES
        const json = (await response.json()) as AgentsListResponse
        const map = new Map<string, AgentAuthorizationStatus>()
        for (const agent of json.agents ?? []) {
          if (typeof agent.needs_authorization === 'boolean') {
            map.set(agent.slug, {
              needsAuthorization: agent.needs_authorization,
              integrationId: agent.id,
            })
          }
        }
        return map
      } catch {
        return EMPTY_STATUSES
      }
    },
  })

  return {data: data ?? EMPTY_STATUSES, isLoading}
}
